import { DownloadPdfButtonProps } from 'src/Pages/Calculator/DownloadPdfButton.tsx'
import { numberToMoney, subtractVatFromPrice } from 'src/Helpers/functions.tsx'
import { VatVisibilityEnum } from 'src/Pages/Calculator/helpers.ts'

export type OfferEquipment = NonNullable<
  DownloadPdfButtonProps['offerEquipments']
>[number]

export interface VatFlags {
  isVAT: boolean
  isNoVat: boolean
  showBothVatVariants: boolean
  priceSuffix: string
}

export interface DetalizationRow {
  name: string
  price: string | number
  quantity: number
  units: string
  total: string
}

export interface DetalizationGroup {
  detalizationSubheader: string
  detalizationRows: DetalizationRow[]
}

export interface CostDetails {
  subheader: string
  totalPriceWithVat: string
  totalPriceWithoutVat: string
  detalization: DetalizationGroup[] | false
}

export interface RelocationRow {
  name: string
  route: string
  deliveryCost?: number
  returnCost?: number
}

export interface RentFeature {
  name: string
  value: string
}

export const getMonthDescription = (numberOfMonth: number): string => {
  if (numberOfMonth === 1) {
    return 'месяц'
  }

  if (numberOfMonth > 4) {
    return 'месяцев'
  }

  return 'месяца'
}

export const getVatFlags = (vatVisibility?: string): VatFlags => {
  const isVAT = vatVisibility === VatVisibilityEnum.VAT
  const isNoVat = vatVisibility === VatVisibilityEnum.NO_VAT
  const showBothVatVariants = !isVAT && !isNoVat

  return {
    isVAT,
    isNoVat,
    showBothVatVariants,
    priceSuffix: showBothVatVariants ? '' : isNoVat ? ' без НДС' : ' с НДС',
  }
}

interface CostDetailsOptions {
  offerEquipments: OfferEquipment[]
  vatRate: number
  isVAT: boolean
  isNoVat: boolean
}

interface HourlyRowsOptions {
  name: string
  pricePerHour: number
  workHours: number
  vatRate: number
  isVAT: boolean
  isNoVat: boolean
}

const buildHourlyRows = ({
  name,
  pricePerHour,
  workHours,
  vatRate,
  isVAT,
  isNoVat,
}: HourlyRowsOptions): DetalizationRow[] => {
  const priceWithoutVat = subtractVatFromPrice(pricePerHour, vatRate)

  return [
    ...(!isVAT
      ? [
          {
            name: `${name} без НДС`,
            price: numberToMoney(priceWithoutVat),
            quantity: workHours,
            units: 'м/ч',
            total: numberToMoney(priceWithoutVat * workHours),
          },
        ]
      : []),
    ...(!isNoVat
      ? [
          {
            name: `${name} с НДС`,
            price: numberToMoney(pricePerHour),
            quantity: workHours,
            units: 'м/ч',
            total: numberToMoney(pricePerHour * workHours),
          },
        ]
      : []),
  ]
}

export const buildCostDetails = ({
  offerEquipments,
  vatRate,
  isVAT,
  isNoVat,
}: CostDetailsOptions): CostDetails[] =>
  offerEquipments.map((item) => ({
    subheader: item.name,
    totalPriceWithVat: item.priceWithVat,
    totalPriceWithoutVat: item.priceWithoutVat,
    detalization: item.detalization
      ? [
          {
            detalizationSubheader: item.name,
            detalizationRows: [
              ...buildHourlyRows({
                name: 'Стоимость работы',
                pricePerHour: item.baseRentalCost,
                workHours: item.workHoursMonthShifts,
                vatRate,
                isVAT,
                isNoVat,
              }),
              ...(item.toolWorkPrice
                ? buildHourlyRows({
                    name: `Стоимость работы навесного (${item.toolName})`,
                    pricePerHour: item.toolWorkPrice,
                    workHours: item.workHoursMonthShifts,
                    vatRate,
                    isVAT,
                    isNoVat,
                  })
                : []),
              {
                name: 'Стоимость доставки запчастей',
                price: numberToMoney(item.partDeliveryPrice),
                quantity: 1,
                units: 'мес',
                total: numberToMoney(Number(item.partDeliveryPrice)),
              },
            ],
          },
          {
            detalizationSubheader: 'Персонал',
            detalizationRows: [
              {
                name: 'Стоимость доставки персонала к месту работ',
                price: numberToMoney(item.personDeliveryPrice),
                quantity: item.shiftsCount,
                units: 'шт',
                total: numberToMoney(
                  item.shiftsCount * item.personDeliveryPrice,
                ),
              },
              {
                name: 'Стоимость проживания на чел/сут',
                price: numberToMoney(item.accommodationPrice),
                quantity: item.shiftsCount * 30,
                units: 'дн',
                total: numberToMoney(
                  item.shiftsCount * 30 * item.accommodationPrice,
                ),
              },
              {
                name: 'Стоимость питания на чел/сут',
                price: numberToMoney(item.foodPrice),
                quantity: item.shiftsCount * 30,
                units: 'дн',
                total: numberToMoney(item.shiftsCount * 30 * item.foodPrice),
              },
              {
                name: 'Стоимость бытового обслуживания',
                price: numberToMoney(item.personnelHouseholdServicesCost),
                quantity: item.shiftsCount * 30,
                units: 'дн',
                total: numberToMoney(
                  item.shiftsCount * 30 * item.personnelHouseholdServicesCost,
                ),
              },
              {
                name: 'Стоимость ежедневного медосмотра',
                price: numberToMoney(item.dailyMedCheckCost),
                quantity: item.shiftsCount * 30,
                units: 'дн',
                total: numberToMoney(
                  item.shiftsCount * 30 * item.dailyMedCheckCost,
                ),
              },
            ],
          },
          {
            detalizationSubheader: 'Затраты на топливо',
            detalizationRows: [
              {
                name: 'Расход топлива',
                price: item.fuelPrice,
                quantity: item.workHoursMonthShifts * item.fuelConsumption,
                units: 'л',
                total: numberToMoney(
                  item.workHoursMonthShifts *
                    item.fuelConsumption *
                    item.fuelPrice,
                ),
              },
            ],
          },
        ]
      : false,
  }))

export const buildRelocationRows = (
  offerEquipments: OfferEquipment[],
): RelocationRow[] =>
  offerEquipments.flatMap((item) =>
    (item.units || [])
      .map((unit) => {
        const routeParts = [
          unit.departurePoint,
          unit.workPoint,
          unit.returnPoint,
        ].filter((part) => part && part.trim())
        const quantity = Math.max(unit.quantity ?? 1, 1)

        return {
          name: quantity > 1 ? `${item.name} (${quantity} ед.)` : item.name,
          route: routeParts.join(' – '),
          deliveryCost: unit.deliveryCost,
          returnCost: unit.returnCost,
        }
      })
      .filter((row) => row.route || row.deliveryCost || row.returnCost),
  )

interface RentFeaturesOptions {
  firstEquipment?: OfferEquipment
  minRentPeriod: number
  isMobDemob?: boolean
  hasRelocation: boolean
}

export const buildRentFeatures = ({
  firstEquipment,
  minRentPeriod,
  isMobDemob,
  hasRelocation,
}: RentFeaturesOptions): RentFeature[] => [
  ...(isMobDemob
    ? [
        {
          name: 'Транспортировка техники до рабочей площадки и обратно на базу АО «РУСНЕДРА МЕНЕДЖМЕНТ»',
          value: hasRelocation ? 'За счет Арендатора' : 'За счет Арендодателя',
        },
      ]
    : []),
  {
    name: 'Минимальный срок аренды',
    value: `${minRentPeriod} ${getMonthDescription(minRentPeriod)}`,
  },
  {
    name: 'Проживание операторов',
    value: firstEquipment?.accommodationPrice
      ? 'За счет Арендодателя'
      : 'За счет Арендатора',
  },
  {
    name: 'Питание операторов',
    value: firstEquipment?.foodPrice
      ? 'За счет Арендодателя'
      : 'За счет Арендатора',
  },
  {
    name: 'Обеспечение ГСМ',
    value: firstEquipment?.fuelPrice
      ? 'За счет Арендодателя'
      : 'За счет Арендатора',
  },
  {
    name: 'Аванс при работе в одну смену',
    value: 'за 150 часов',
  },
  {
    name: 'Аванс при работе в две смены',
    value: 'за 300 часов',
  },
]

export const includedExpensesData = [
  'Амортизационные отчисления оборудования',
  'Эксплуатацию, техническое обслуживание и инспекции',
  'Текущий и капитальный ремонт ТС, запасные части и расходные материалы',
  'Мойку и чистку, размещение ТС в гараже и/или затраты на парковку',
  'Внутренний трехэтапный отбор машинистов техники для качественного оказания услуг',
  'Расходы на оплату труда операторов',
  'Расходы на проезд операторов до места выполнения работ и обратно',
  'Меры по охране труда',
]

export const equipmentConditionsData = [
  'Техника не старше 3 лет и обновляется ежегодно',
  'Оборудована системой ГЛОНАСС с wi-fi модулями',
  'Двусторонними видеорегистраторами',
  'Пакетом для эксплуатации в Северных районах.',
  'Необходимыми разрешениями: ЧТО, ПТО, Ростехнадзор, ОПО, лицензия на пассажироперевозки, ДОПОГ и т.д.',
]

interface OtherConditionsOptions {
  isWorkService?: boolean
  medicalExamination?: boolean
}

export const buildOtherConditions = ({
  isWorkService,
  medicalExamination,
}: OtherConditionsOptions): Array<{ text: string; checked?: boolean }> => [
  {
    text: 'Выполнение требований законодательства и нормативных правовых актов, в том числе в области санитарной и эпидемиологической безопасности, проведение инструктажей',
    checked: true,
  },
  {
    text: 'Расходы по получению и поддержанию в силе любой разрешительной документации, необходимой для оказания услуг',
    checked: true,
  },
  {
    text: 'Командировочные расходы, расходы на страхование техники и персонала, предусмотренные законодательством РФ налоги и сборы',
    checked: true,
  },
  { text: 'Электронный документооборот', checked: true },
  { text: 'Обеспечение нормальных условий труда', checked: isWorkService },
  {
    text: 'Медицинское освидетельствование водителей',
    checked: medicalExamination,
  },
  { text: 'Банковские проценты', checked: false },
]
